import { Box, Card, CardContent, CardHeader, Skeleton, Stack, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { GenealogyAPI } from '../Genealogy/GenealogyAPIs'
import storeAuth from 'GlobalStores/User/StoreAuth'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import { AchievementAPI } from './AchievementAPI'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { get, has, lowerCase } from 'lodash'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import { getNativeName } from 'Helpers/GlobalHelpers'
import moment from 'moment'
import { ReactComponent as PhoneIcon } from './assets/PhoneIcon.svg'
import { ReactComponent as MailIcon } from './assets/MailIcon.svg'
import storeReferral from 'GlobalStores/Referral/StoreReferral'
import { GetReferralCode } from '../Invite/ReferralLinks/APIs'
import { autorun, reaction, runInAction } from 'mobx'
import { ReactComponent as CopyIcon } from './assets/CopyIcon.svg'
import { ReactComponent as CopiedIcon } from './assets/CopiedIcon.svg'
import copy from 'copy-to-clipboard'
import Countries from '../../../lib/country/country2letters.json'
import { ReactComponent as RankStarIcon } from './assets/RankStarIcon.svg'
import StoreAchievement from './StoreAchievement'
import GenealogyProfilePicture from '../Genealogy/GenealogyProfilePicture'

const InfoRow = ({ label, value }) => (
    <Stack direction={'row'} gap="7px">
        <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(0, 59, 111, 1)" fontWeight={400} whiteSpace={'nowrap'}>
            {label}:
        </Typography>
        <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(112, 119, 131, 1)" fontWeight={400}>
            {value}
        </Typography>
    </Stack>
)

const ProfileSkeleton = ({ sx }) => (
    <Card sx={{ '&.MuiPaper-root': { borderRadius: '8px' }, ...sx }}>
        <CardHeader
            title={<Skeleton variant="text" width={120} height={24} sx={{ margin: '0 auto', bgcolor: 'rgba(255,255,255,0.2)' }} />}
            sx={{
                backgroundColor: 'rgba(0, 59, 111, 1)',
                paddingY: '12px'
            }}
        />
        <CardContent sx={{ padding: '24px 17px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Stack gap="24px" borderBottom={'1px solid rgba(204, 222, 239, 1)'} pb="24px">
                {/* Profile Picture and Name */}
                <Stack direction={'row'} gap={'20px'}>
                    <Skeleton variant="rounded" width={45} height={45} sx={{ borderRadius: '12px' }} />
                    <Stack justifyContent={'space-between'} flex={1}>
                        <Skeleton variant="text" width="70%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Stack>
                </Stack>
                {/* Sponsor Info Box */}
                <Stack bgcolor={'rgba(255, 241, 220, 1)'} borderRadius={'8px'} gap={'4px'} padding="8px 16px">
                    <Stack direction={'row'} gap="7px">
                        <Skeleton variant="text" width={80} height={18} />
                        <Skeleton variant="text" width={120} height={18} />
                    </Stack>
                    <Stack direction={'row'} gap="7px">
                        <Skeleton variant="text" width={80} height={18} />
                        <Skeleton variant="text" width={100} height={18} />
                    </Stack>
                </Stack>
                {/* Contact Info */}
                <Stack gap="8px">
                    {/* Rank */}
                    <Stack direction={'row'} gap="16px" alignItems="center">
                        <Skeleton variant="rounded" width={20} height={20} />
                        <Skeleton variant="text" width={80} height={18} />
                    </Stack>
                    {/* Phone */}
                    <Stack direction={'row'} gap="16px" alignItems="center">
                        <Skeleton variant="rounded" width={20} height={20} />
                        <Skeleton variant="text" width={120} height={18} />
                    </Stack>
                    {/* Email */}
                    <Stack direction={'row'} gap="16px" alignItems="center">
                        <Skeleton variant="rounded" width={20} height={20} />
                        <Skeleton variant="text" width={180} height={18} />
                    </Stack>
                    {/* Country */}
                    <Stack direction={'row'} gap="16px" alignItems="center">
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width={100} height={18} />
                    </Stack>
                </Stack>
            </Stack>
            {/* Referral Link Section */}
            <Stack gap="8px">
                <Stack gap="16px">
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="text" width="90%" height={20} />
                </Stack>
                <Stack
                    sx={{
                        backgroundColor: 'rgba(247, 249, 253, 1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(204, 222, 239, 1)',
                        padding: '16px',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                    <Skeleton variant="text" width="80%" height={18} />
                    <Skeleton variant="circular" width={24} height={24} />
                </Stack>
            </Stack>
        </CardContent>
    </Card>
)

const Profile = observer(props => {
    const translate = useTranslate()
    const customerId = props.customerId || storeAuth.GetId()
    const isOwnProfile = customerId === storeAuth.GetId()

    const [achievements, setAchievements] = useState([])
    const [centurion, setCenturion] = useState({
        centurionHistory: {
            items: []
        },
        humanName: {
            fullName: ''
        },
        id: {
            unicity: ''
        }
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [profile, setProfile] = useState(null)
    const [isCopied, setIsCopied] = useState(false)

    const getAchievements = async () => {
        try {
            const responseAchievements = await GenealogyAPI.GetGenealogyItems({
                baId: customerId,
                ushopCountryCode: storeCountry.Country3(),
                token: storeAuth.GetToken(),
                customerType: 'all'
            })

            StoreAchievement.setGenealogyItems(responseAchievements.data.ushop.genealogy.items)
            StoreAchievement.setAchievements(responseAchievements.data.ushop.achievementsHistory.items)

            const periods = []
            const ranks = []
            responseAchievements.data.ushop.achievementsHistory.items.forEach((item, index) => {
                periods.push(item.period)
                if (index === 0) {
                    item.value.map(val => {
                        if (val.achievement.rankShort) {
                            ranks.push(val.achievement.rankShort)
                        }
                    })
                }
            })
            StoreAchievement.periods = periods
            StoreAchievement.ranks = ranks

            const showProfile = responseAchievements.data.ushop.genealogy.items.find(item => item.customer.id.unicity === customerId)
            setProfile(showProfile)

            if (isOwnProfile) {
                initReferralData()
            }
            setAchievements(responseAchievements.data.ushop.achievementsHistory.items)
            StoreAchievement.currentCenturionProfileId = customerId
            setCenturion(centurion.data)
            setLoading(false)
        } catch (error) {
            console.error(error)
            setError(error)
            setLoading(false)
        }
    }

    const getCenturion = async () => {
        try {
            StoreAchievement.setIsLoadingCenturion(true)
            const centurionResponse = await AchievementAPI.getCenturion({
                baId: customerId,
                period: StoreAchievement.getPeriodWithMonthDigit(),
                countryCode: storeCountry.Country3(),
                token: storeAuth.GetToken()
            })

            StoreAchievement.setCenturions(StoreAchievement.getPeriodWithMonthDigit(), centurionResponse.data.centurionHistory.items)
            StoreAchievement.setIsLoadingCenturion(false)
        } catch (error) {
            console.error(error)
            setError(error)
            setLoading(false)
            StoreAchievement.setIsLoadingCenturion(false)
        }
    }

    const initReferralData = useCallback(async () => {
        try {
            const { data } = await GetReferralCode(storeAuth.GetToken())

            runInAction(() => {
                storeReferral.referralData = get(data, 'items[0]', null)
                storeReferral.loadingReferCode = false
            })
        } catch (error) {
            console.log(error)
        }
    }, [storeReferral.referralData])

    const handleCopy = () => {
        if (!isCopied) {
            copy(`https://ufeelgreat.com/c/${storeReferral.referralData?.referralCode}`)
            setIsCopied(true)
            setTimeout(() => {
                setIsCopied(false)
            }, 5000)
        }
    }

    useEffect(() => {
        if (loading) {
            getAchievements()
        }
    }, [loading])

    useEffect(() => {
        if (achievements.length === 0) {
            setLoading(true)
        }
    }, [achievements])

    useEffect(() => {
        const disposePeriod = reaction(
            () => StoreAchievement.getPeriodWithMonthDigit(),
            n => {
                if (n) {
                    getCenturion()
                }
            }
        )

        return () => {
            disposePeriod()
            StoreAchievement.setSelectedPeriod(0)
            StoreAchievement.setPeriods([])
            StoreAchievement.setRanks([])
            StoreAchievement.setAchievements([])
            StoreAchievement.setGenealogyItems([])
            StoreAchievement.setCenturions({})
            StoreAchievement.setIsLoadingCenturion(false)
            StoreAchievement.setCurrentCenturionProfileId('')
        }
    }, [])

    const getProfilePicture = profile => {
        return profile?.customer?.profilePicture?.sizes?.find(item => item.size === 'original')?.media || ''
    }

    const getProfileName = profile => {
        return profile ? (StoreTranslate.CurrentLanguage() === 'EN' ? profile?.customer.humanName.fullName : getNativeName(profile?.customer.humanName)) : ''
    }

    const getProfileSponsor = profile => {
        return profile
            ? StoreTranslate.CurrentLanguage() === 'EN'
                ? profile?.customer.sponsor.humanName.fullName
                : getNativeName(profile?.customer.sponsor.humanName)
            : ''
    }

    const getProfilePhone = profile => {
        if (profile) {
            if (has(profile, 'customer.mobilePhone')) {
                return profile.customer.mobilePhone || '-'
            } else if (has(profile, 'customer.homePhone')) {
                return profile.customer.homePhone || '-'
            } else if (has(profile, 'customer.workPhone')) {
                return profile.customer.workPhone || '-'
            }
        }
        return '-'
    }

    const getProfileCountry = profile => {
        return profile ? Countries[profile?.customer.mainAddress.country] || profile?.customer.mainAddress.country : ''
    }

    const getProfileCityCountry = profile => {
        let city = ''
        let country = ''
        if (profile) {
            city = profile?.customer.mainAddress.city
            country = getProfileCountry(profile)
        }

        return city ? city + ', ' + country : country
    }

    if (loading) {
        return (
            <ProfileSkeleton sx={{ display: props.display ? props.display : 'inherit', flexDirection: props.flexDirection ? props.flexDirection : 'column' }} />
        )
    }

    return (
        <Card
            sx={{
                display: props.display ? props.display : 'inherit',
                flexDirection: props.flexDirection ? props.flexDirection : 'column',
                borderRadius: '8px'
            }}
            elevation={0}>
            <CardHeader
                title={
                    <Typography variant="h6" color="#ffffff" fontSize={'18px'} fontWeight={400} textAlign={'center'} lineHeight={'1'}>
                        {profile && translate(`${lowerCase(profile?.customer.cumulativeMetricsProfile.highestRankShort).replace(/ /g, '')}_rank`)}
                    </Typography>
                }
                sx={{
                    backgroundColor: 'rgba(0, 59, 111, 1)',
                    paddingY: '12px'
                }}
            />
            <CardContent sx={{ padding: '24px 17px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Stack gap="24px" borderBottom={isOwnProfile ? '1px solid rgba(204, 222, 239, 1)' : '0px'} pb={isOwnProfile ? '24px' : '0px'}>
                    <Stack direction={'row'} gap={'20px'} display={'grid'} gridTemplateColumns={'45px 1fr'}>
                        <GenealogyProfilePicture
                            profilePicture={getProfilePicture(profile)}
                            profileRankShort={profile?.customer.cumulativeMetricsProfile.highestRankShort}
                            avatarStyle="rectangle"
                        />
                        <Stack justifyContent={'space-between'}>
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'16px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {getProfileName(profile)}
                            </Typography>
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'12px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {profile?.customer.id.unicity}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack bgcolor={'rgba(255, 241, 220, 1)'} borderRadius={'8px'} gap={'4px'} padding="8px 16px" width={'100%'}>
                        <InfoRow label={translate('sponsor_id')} value={getProfileSponsor(profile)} />
                        <InfoRow label={translate('enroller_on')} value={profile && moment(profile?.customer.joinDate).format('DD MMM YYYY')} />
                    </Stack>
                    <Stack gap="8px">
                        <Stack direction={'row'} gap="16px">
                            <RankStarIcon />
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {profile && translate(`${lowerCase(profile?.customer.cumulativeMetricsProfile.highestRankShort).replace(/ /g, '')}_rank`)}
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} gap="16px">
                            <PhoneIcon />
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {getProfilePhone(profile)}
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} gap="16px">
                            <MailIcon />
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {profile?.customer?.email || '-'}
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} gap="16px">
                            <Box
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    backgroundImage: `url(${`https://ushop-media.unicity.com/images/flags/${profile?.customer.mainAddress.country}.svg`})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}></Box>
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'14px'} color="rgba(0, 59, 111, 1)" fontWeight={400}>
                                {getProfileCityCountry(profile)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                {isOwnProfile && (
                    <Stack gap="8px">
                        <Stack gap="16px">
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'16px'} color="rgba(0, 59, 111, 1)" fontWeight={700}>
                                {translate('referral_link')}
                            </Typography>
                            <Typography fontFamily={'Inter, Poppins, NotoSansThai'} fontSize={'16px'} color="rgba(90, 143, 195, 1)" fontWeight={400}>
                                {translate('referral_link_description')}
                            </Typography>
                        </Stack>
                        <Stack
                            sx={{
                                backgroundColor: 'rgba(247, 249, 253, 1)    ',
                                borderRadius: '8px',
                                border: '1px solid rgba(204, 222, 239, 1)',
                                padding: '16px',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: 'Inter, Poppins, NotoSansThai',
                                    fontSize: '14px',
                                    color: 'rgba(0, 59, 111, 1)',
                                    fontWeight: 400,
                                    lineHeight: 1
                                }}>
                                https://ufeelgreat.com/c/{storeReferral.referralData?.referralCode}
                            </Typography>
                            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} sx={{ cursor: 'pointer' }} onClick={handleCopy}>
                                {isCopied ? <CopiedIcon /> : <CopyIcon />}
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </CardContent>
        </Card>
    )
})

export default Profile
